import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/common/Layout/Navbar";
import "./Register.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[\d\s()-]{7,20}$/;

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    resendVerificationEmail,
    user,
    loading: authLoading,
  } = useAuth();
  const errorRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    businessName: "",
    optionalEmailUpdatesEnabled: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationResendAvailable, setVerificationResendAvailable] =
    useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    special: false,
  });

  // Modal State
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isTermsModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isTermsModalOpen]);

  useEffect(() => {
    if (verificationSent || authLoading || !user) return;
    if (user.Role === "admin") {
      navigate(
        location.state?.onboardingType === "website"
          ? "/admin/website-packages"
          : location.state?.onboardingType === "ecommerce"
            ? "/admin/packages"
            : "/admin",
        { replace: true },
      );
      return;
    }
    if (location.state?.onboardingType === "website") {
      const packageId = location.state.websitePackageId;
      navigate(
        `/website-onboarding${packageId ? `?packageId=${encodeURIComponent(packageId)}` : ""}`,
        { replace: true },
      );
      return;
    }
    if (location.state?.onboardingType === "ecommerce") {
      navigate("/request-store", {
        replace: true,
        state: {
          selectedPackage: location.state.selectedPackage,
          trial: location.state.trial,
        },
      });
      return;
    }
    navigate("/dashboard", { replace: true });
  }, [authLoading, location.state, navigate, user, verificationSent]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "password") {
      validatePassword(value);
    }

    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    if (error) setError("");
  };

  // ============================================================
  // Password Validation Functions
  // ============================================================

  const validatePassword = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const isPasswordValid = () => {
    return (
      passwordStrength.length &&
      passwordStrength.uppercase &&
      passwordStrength.lowercase &&
      passwordStrength.digit &&
      passwordStrength.special
    );
  };

  const getPasswordStrengthText = () => {
    if (formData.password.length === 0) return { text: "", color: "#9ca3af" };

    const validCount = Object.values(passwordStrength).filter(Boolean).length;

    if (validCount === 5) return { text: "Strong", color: "#10b981" };
    if (validCount >= 3) return { text: "Medium", color: "#f59e0b" };
    return { text: "Weak", color: "#ef4444" };
  };

  const getPasswordStrengthPercent = () => {
    const validCount = Object.values(passwordStrength).filter(Boolean).length;
    return (validCount / 5) * 100;
  };

  const togglePasswordVisibility = () => setShowPassword((v) => !v);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword((v) => !v);

  // ============================================================
  // Validation
  // ============================================================

  const validate = () => {
    const errors = {};

    if (!formData.fullName.trim()) errors.fullName = "Full name is required.";
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      errors.email = "Enter a valid email address.";
    }
    if (formData.phone.trim() && !PHONE_REGEX.test(formData.phone.trim())) {
      errors.phone = "Enter a valid phone number.";
    }
    if (!formData.password) {
      errors.password = "Password is required.";
    } else if (!isPasswordValid()) {
      errors.password = "Password does not meet the requirements below.";
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
    if (!agreedToTerms) {
      errors.terms = "You must agree to the terms to continue.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============================================================
  // Submit Handler
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validate()) {
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }

    setLoading(true);

    try {
      const userData = {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        mobileNumber: formData.phone.trim(),
        businessName: formData.businessName.trim(),
        optionalEmailUpdatesEnabled: formData.optionalEmailUpdatesEnabled,
      };
      await register(userData);
      setVerificationSent(true);
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
      setVerificationResendAvailable(
        String(err.message || "")
          .toLowerCase()
          .includes("verification email could not be sent"),
      );
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setError("");
    setResendMessage("");
    setResendingVerification(true);
    try {
      const response = await resendVerificationEmail(formData.email.trim());
      setResendMessage(response.message);
    } catch (resendError) {
      setError(resendError.message);
    } finally {
      setResendingVerification(false);
    }
  };

  const strength = getPasswordStrengthText();

  return (
    <>
      <Navbar variant="auth" />

      <main className="auth-container">
        <div className="auth-card register-card">
          {/* Header */}
          <div className="auth-header">
            <h1>Create account</h1>
            <p>Join Toora Flex and get started today</p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="alert alert-error"
              role="alert"
              aria-live="assertive"
              tabIndex={-1}
              ref={!Object.keys(fieldErrors).length ? errorRef : undefined}
            >
              {error}
              {verificationResendAvailable && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                >
                  {resendingVerification
                    ? "Sending..."
                    : "Retry verification email"}
                </button>
              )}
              {resendMessage && (
                <p role="status" className="verification-resend-message">
                    {resendMessage}
                </p>
              )}
            </div>
          )}

          {verificationSent ? (
            <div className="alert alert-success" role="status">
              Your account has been created. We&apos;re sending a verification
              email to <strong>{formData.email}</strong>. It may take a few
              minutes to arrive, so check your spam or junk folder too. Open the
              link in that email to verify your account and sign in
              automatically.
              <p>
                Already verified? <Link to="/login">Sign in</Link>
              </p>
              <button
                type="button"
                className="btn-primary"
                onClick={handleResendVerification}
                disabled={resendingVerification}
              >
                {resendingVerification
                  ? "Sending..."
                  : "Resend verification email"}
              </button>
              {resendMessage && (
                <p role="status" className="verification-resend-message">
                  {resendMessage}
                </p>
              )}
            </div>
          ) : (
          /* Registration Form */
          <form onSubmit={handleSubmit} className="register-form" noValidate>
            <div className="register-form-grid">
              {/* Full Name */}
              <div className="form-group full-width">
                <label htmlFor="fullName">Full name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  autoFocus
                  disabled={loading}
                  aria-invalid={Boolean(fieldErrors.fullName)}
                  className={fieldErrors.fullName ? "error" : ""}
                  required
                />
                {fieldErrors.fullName && (
                  <span className="error-text">{fieldErrors.fullName}</span>
                )}
              </div>

              {/* Email */}
              <div className="form-group full-width">
                <label htmlFor="email">Email address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  disabled={loading}
                  aria-invalid={Boolean(fieldErrors.email)}
                  className={fieldErrors.email ? "error" : ""}
                  required
                />
                {fieldErrors.email && (
                  <span className="error-text">{fieldErrors.email}</span>
                )}
              </div>

              {/* Phone */}
              <div className="form-group full-width">
                <label htmlFor="phone">
                  Phone number{" "}
                  <span className="optional-label">(optional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  autoComplete="tel"
                  disabled={loading}
                  aria-invalid={Boolean(fieldErrors.phone)}
                  className={fieldErrors.phone ? "error" : ""}
                />
                {fieldErrors.phone && (
                  <span className="error-text">{fieldErrors.phone}</span>
                )}
              </div>

              {/* Business Name */}
              <div className="form-group full-width">
                <label htmlFor="businessName">
                  Business name{" "}
                  <span className="optional-label">(optional)</span>
                </label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  placeholder="Enter your business name"
                  autoComplete="organization"
                  disabled={loading}
                />
              </div>

              {/* Password */}
              <div className="form-group full-width password-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password"
                    autoComplete="new-password"
                    minLength={8}
                    disabled={loading}
                    aria-invalid={Boolean(fieldErrors.password)}
                    required
                    className={
                      formData.password.length > 0
                        ? isPasswordValid()
                          ? "valid"
                          : "invalid"
                        : ""
                    }
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={togglePasswordVisibility}
                    tabIndex={-1}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    <i
                      className={
                        showPassword ? "fas fa-eye-slash" : "fas fa-eye"
                      }
                    ></i>
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {formData.password.length > 0 && (
                  <div className="password-strength-container">
                    <div className="password-strength-bar">
                      <div
                        className="password-strength-fill"
                        style={{
                          width: `${getPasswordStrengthPercent()}%`,
                          background: strength.color,
                        }}
                      />
                    </div>
                    <div className="password-strength-text">
                      <span style={{ color: strength.color }}>
                        {strength.text}
                      </span>
                    </div>
                  </div>
                )}

                {/* Password Requirements Checklist */}
                <div className="password-requirements">
                  <ul>
                    <li className={passwordStrength.length ? "met" : "unmet"}>
                      <span className="requirement-icon" aria-hidden="true">
                        {passwordStrength.length ? "✓" : "○"}
                      </span>
                      At least 8 characters
                    </li>
                    <li
                      className={passwordStrength.uppercase ? "met" : "unmet"}
                    >
                      <span className="requirement-icon" aria-hidden="true">
                        {passwordStrength.uppercase ? "✓" : "○"}
                      </span>
                      At least 1 uppercase letter
                    </li>
                    <li
                      className={passwordStrength.lowercase ? "met" : "unmet"}
                    >
                      <span className="requirement-icon" aria-hidden="true">
                        {passwordStrength.lowercase ? "✓" : "○"}
                      </span>
                      At least 1 lowercase letter
                    </li>
                    <li className={passwordStrength.digit ? "met" : "unmet"}>
                      <span className="requirement-icon" aria-hidden="true">
                        {passwordStrength.digit ? "✓" : "○"}
                      </span>
                      At least 1 number
                    </li>
                    <li className={passwordStrength.special ? "met" : "unmet"}>
                      <span className="requirement-icon" aria-hidden="true">
                        {passwordStrength.special ? "✓" : "○"}
                      </span>
                      At least 1 special character (!@#$%^&amp;*)
                    </li>
                  </ul>
                </div>
                {fieldErrors.password && (
                  <span className="error-text">{fieldErrors.password}</span>
                )}
              </div>

              {/* Confirm Password */}
              <div className="form-group full-width password-group">
                <label htmlFor="confirmPassword">Confirm password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    minLength={8}
                    disabled={loading}
                    required
                    className={
                      formData.confirmPassword.length > 0
                        ? formData.password === formData.confirmPassword &&
                          isPasswordValid()
                          ? "valid"
                          : "invalid"
                        : ""
                    }
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={toggleConfirmPasswordVisibility}
                    tabIndex={-1}
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                  >
                    <i
                      className={
                        showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"
                      }
                    ></i>
                  </button>
                </div>
                {formData.confirmPassword.length > 0 &&
                  formData.password !== formData.confirmPassword && (
                    <span className="password-match-error">
                      Passwords do not match
                    </span>
                  )}
                {formData.confirmPassword.length > 0 &&
                  formData.password === formData.confirmPassword && (
                    <span className="password-match-success">
                      ✓ Passwords match
                    </span>
                  )}
              </div>
            </div>

            {/* Terms Checkbox - Single Line */}
            <div className="form-options">
              <label className="checkbox-label single-line">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    if (fieldErrors.terms) {
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.terms;
                        return next;
                      });
                    }
                  }}
                  disabled={loading}
                />
                <span>
                  I agree to the{" "}
                  <button
                    type="button"
                    className="inline-link-btn"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent label default behavior
                      setIsTermsModalOpen(true);
                    }}
                  >
                    Terms and Conditions
                  </button>{" "}
                  and{" "}
                  <button
                    type="button"
                    className="inline-link-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsTermsModalOpen(true);
                    }}
                  >
                    Privacy Policy
                  </button>
                </span>
              </label>
            </div>
            {fieldErrors.terms && (
              <span className="error-text terms-error">
                {fieldErrors.terms}
              </span>
            )}

            <div className="form-options">
              <label className="checkbox-label single-line register-optional-consent">
                <input
                  type="checkbox"
                  checked={formData.optionalEmailUpdatesEnabled}
                  onChange={(event) =>
                    setFormData((current) => ({
                      ...current,
                      optionalEmailUpdatesEnabled: event.target.checked,
                    }))
                  }
                  disabled={loading}
                />
                <span>
                  I would like to receive optional newsletters, promotions, and
                  non-essential product updates by email. I can unsubscribe at
                  any time.
                </span>
              </label>
            </div>
            <p className="register-communications-note">
              Essential account, security, payment, and service notices may
              still be sent while your account or service is active.
            </p>

            {/* Submit */}
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner" aria-hidden="true" />
                  Creating account…
                </span>
              ) : (
                "Create account"
              )}
            </button>
          </form>
          )}

          {/* Footer */}
          <div className="auth-footer">
            <p>
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </div>
        </div>
      </main>

      {/* Terms Modal Overlay */}
      {isTermsModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsTermsModalOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Terms and Conditions</h2>
              <button
                className="modal-close"
                onClick={() => setIsTermsModalOpen(false)}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <h3>1. Acceptance of Terms</h3>
              <p>
                By creating an account and using our services, you agree to be
                bound by these Terms and Conditions. If you do not agree to
                these terms, please do not use our services.
              </p>

              <h3>2. Account Registration</h3>
              <p>
                You must provide accurate and complete information when creating
                your account. You are responsible for maintaining the
                confidentiality of your account credentials and for all
                activities that occur under your account.
              </p>

              <h3>3. User Obligations</h3>
              <p>
                You agree to use our services in compliance with all applicable
                laws and regulations. You shall not:
              </p>
              <ul>
                <li>
                  Use the services for any illegal or unauthorized purpose
                </li>
                <li>Share your account credentials with others</li>
                <li>
                  Attempt to gain unauthorized access to other users' accounts
                </li>
                <li>Upload or transmit malicious code or viruses</li>
                <li>Harass, abuse, or harm other users</li>
              </ul>

              <h3>4. Privacy Policy</h3>
              <p>
                Your privacy is important to us. We collect and process your
                personal information in accordance with our Privacy Policy.
                Acceptance of these Terms does not opt you in to optional
                newsletters, promotions, or non-essential product updates.
              </p>

              <h3>5. Service communications and optional email</h3>
              <p>
                While your account or service is active, we may send essential
                account, security, payment, onboarding, maintenance, service
                interruption, suspension or shutdown, and legal notices.
                Optional newsletters, promotions, and non-essential product
                updates are sent only if you separately opt in. You can
                unsubscribe from optional email at any time; essential service
                notices may continue while your account or service remains
                active.
              </p>

              <h3>6. Account and data deletion requests</h3>
              <p>
                You may submit a request to delete your account or personal
                information through your account profile. Requests are reviewed
                before action is taken, and we may retain information required
                by law or reasonably necessary for accounting, payments,
                contractual obligations, disputes, security, or legal claims.
                Closing an account does not automatically cancel amounts due.
              </p>

              <h3>7. Intellectual Property</h3>
              <p>
                All content, features, and functionality available through our
                services are the exclusive property of our company and are
                protected by intellectual property laws.
              </p>

              <h3>8. Termination</h3>
              <p>
                We reserve the right to suspend or terminate your account at our
                sole discretion, without prior notice, for conduct that we
                believe violates these Terms and Conditions or is harmful to
                other users or our business interests.
              </p>

              <h3>9. Disclaimer of Warranties</h3>
              <p>
                Our services are provided "as is" without warranties of any
                kind, either express or implied. We do not warrant that the
                services will be uninterrupted, error-free, or secure.
              </p>

              <h3>10. Limitation of Liability</h3>
              <p>
                To the fullest extent permitted by law, we shall not be liable
                for any indirect, incidental, special, consequential, or
                punitive damages arising from your use of our services.
              </p>

              <h3>11. Changes to Terms</h3>
              <p>
                We reserve the right to update or modify these Terms and
                Conditions at any time. We will notify you of any material
                changes via email or through our services.
              </p>

              <h3>12. Contact Information</h3>
              <p>
                If you have any questions about these Terms and Conditions,
                please contact us at:
                <br />
                Email:{" "}
                <a href="mailto:info@tooraflex.co.za">info@tooraflex.co.za</a>
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-primary"
                onClick={() => setIsTermsModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Register;
